import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, BookOpen, Image, ShoppingBag, Users, Phone, Heart, Shield, Instagram, Youtube, Sun, Moon, LogOut, MessageCircle, MapPin, ChevronDown } from 'lucide-react';
import { XIcon } from '../ui/XIcon';
import PadaidLogo from '../ui/PadaidLogo';
import AnnouncementBanner from './AnnouncementBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { adminGetMaintenance, adminSaveMaintenance } from '../../services/site';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [maint, setMaint] = useState(false);
  const [maintLoading, setMaintLoading] = useState(false);
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const isFounder = !!user && user.user_type === 'fondateur';
  const isLoggedIn = !!user;
  const isHome = location.pathname === '/home' || location.pathname === '/';

  // Masquage auto de la barre quand on entre dans un sous-menu ; réapparaît en haut d'écran ou à l'accueil
  const [navHidden, setNavHidden] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const armedRef = React.useRef(false);

  const armSubmenu = () => { armedRef.current = true; };
  const handleNavMouseLeave = () => {
    if (armedRef.current && !isHome) setNavHidden(true);
  };
  const hideOnSubItem = () => {
    armedRef.current = true;
    if (!isHome) setNavHidden(true);
    setIsOpen(false);
  };

  // Toujours visible sur l'accueil
  useEffect(() => {
    if (isHome) { setNavHidden(false); armedRef.current = false; }
  }, [location.pathname, isHome]);

  // Révéler la barre quand la souris remonte tout en haut, ou en haut du scroll (mobile)
  useEffect(() => {
    const onMove = (e) => { if (e.clientY <= 6) setNavHidden(false); };
    const onScroll = () => { if (window.scrollY <= 4) setNavHidden(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchstart', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchstart', onScroll);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const handleLogout = async () => {
    try { await logout(); } catch (e) { /* ignore */ }
    window.location.href = '/';
  };

  const navigation = [
    { name: t('nav.home'), href: '/home', icon: Home, children: [
      { name: t('child.alt_view'), href: '/home-alt' },
      { name: t('child.mobile'), href: '/mobile' },
    ]},
    { name: t('nav.blog'), href: '/blog', icon: BookOpen, children: [
      { name: t('child.faq'), href: '/faq' },
    ]},
    { name: t('nav.gallery'), href: '/gallery', icon: Image, children: [
      { name: t('child.our_media'), href: '/gallery' },
    ]},
    { name: t('nav.shop'), href: '/shop', icon: ShoppingBag, children: [
      { name: t('child.membership'), href: '/membership' },
      { name: t('child.donate'), href: '/donate' },
    ]},
    { name: t('nav.membership'), href: '/membership', icon: Users, children: [
      { name: t('child.become_member'), href: '/membership' },
      { name: t('child.support'), href: '/payment-v2' },
    ]},
    { name: t('nav.team'), href: '/equipe', icon: Users, children: [
      { name: t('child.discover_team'), href: '/equipe' },
    ]},
    { name: t('nav.donate'), href: '/donate', icon: Heart, children: [
      { name: t('child.donate_now'), href: '/payment-v2' },
      { name: t('child.contact_us'), href: '/contact' },
    ]},
    { name: t('nav.contact'), href: '/contact', icon: Phone, children: [
      { name: t('child.contact_form'), href: '/contact' },    ]},
    { name: t('nav.carte'), href: '/carte', icon: MapPin, children: [
      { name: t('child.intervention_zones'), href: '/carte' },
    ]},
    { name: t('nav.faq'), href: '/faq', icon: BookOpen, children: [
      { name: t('child.faq_questions'), href: '/faq' },
    ]},
  ];
  useEffect(() => {
    const loadMaint = async () => {
      if (!user || user.user_type !== 'fondateur') return;
      try {
        const res = await adminGetMaintenance(token);
        setMaint(!!res.enabled);
      } catch (e) {
        // ignore
      }
    };
    loadMaint();
  }, [user, token]);


  return (
    <>
      <AnnouncementBanner />
      <nav
        onMouseLeave={handleNavMouseLeave}
        className={`bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border sticky top-0 z-50 transition-transform duration-300 ${navHidden ? '-translate-y-full' : 'translate-y-0'}`}
        data-testid="main-navbar"
      >
        <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16">
          {/* Rangée 1 : logo centré en haut + utilitaires */}
          <div className="relative grid grid-cols-[1fr_auto_1fr] items-center h-16 gap-2">
            {/* Gauche */}
            <div className="flex items-center gap-2 min-w-0">
              {/* Mobile : langue + thème + menu */}
              <div className="md:hidden flex items-center gap-1">
                <LanguageSwitcher compact />
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-full border border-border text-muted-foreground hover:text-primary"
                  title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
                  aria-label="Basculer le thème clair/sombre"
                >
                  {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                </button>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="text-muted-foreground hover:text-primary focus:outline-none p-2"
                  aria-label="Ouvrir le menu"
                >
                  {isOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
              </div>
              {/* Desktop : réseaux sociaux */}
              <div className="hidden lg:flex items-center gap-3">
                <a href="https://x.com/Padaid_Asso" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" aria-label="X (Twitter)"><XIcon size={16} /></a>
                <a href="https://www.instagram.com/padaid_asso" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" aria-label="Instagram"><Instagram size={18} /></a>
                <a href="https://www.youtube.com/@PADAID" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" aria-label="YouTube"><Youtube size={18} /></a>
              </div>
            </div>

            {/* Centre : logo PADAID centré */}
            <div className="flex justify-center">
              <PadaidLogo size="md" showText={true} />
            </div>

            {/* Droite : utilitaires (desktop) */}
            <div className="hidden md:flex items-center justify-end gap-2 flex-wrap min-w-0">
              <Link
                to="/payment-v2"
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200 text-sm font-medium whitespace-nowrap"
              >
                {t('common.support_padaid')}
              </Link>
              {isLoggedIn && (
                <Link
                  to="/messages"
                  className="relative p-2 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-all duration-300 hover:scale-110"
                  title="Messagerie"
                  aria-label="Messagerie"
                  data-testid="nav-messages-link"
                >
                  <MessageCircle size={18} />
                </Link>
              )}
              {isLoggedIn ? (
                <button
                  onClick={handleLogout}
                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors duration-200 text-sm font-medium px-3 py-2 rounded-md flex items-center gap-2 whitespace-nowrap"
                  title="Se déconnecter"
                  data-testid="logout-btn"
                >
                  <LogOut size={16} /> {t('common.logout')}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors duration-200 text-sm font-medium px-3 py-2 rounded-md flex items-center gap-2 whitespace-nowrap"
                  title="Espace Admin"
                  data-testid="admin-login-link"
                >
                  <Shield size={16} /> {t('common.admin')}
                </Link>
              )}
              <LanguageSwitcher />
              <button
                onClick={toggleTheme}
                className="relative p-2 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-all duration-300 hover:scale-110"
                title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
                aria-label="Basculer le thème clair/sombre"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </div>
          </div>

          {/* Rangée 2 : navigation (desktop) — barre pleine largeur segmentée et élégante */}
          <div className="hidden md:block pb-2" data-testid="main-navbar-desktop">
            <div className="flex items-stretch w-full rounded-xl border border-border/60 bg-muted/20 overflow-hidden shadow-sm">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <div
                    key={item.name}
                    className="relative group flex-1 min-w-0 border-r border-border/40 last:border-r-0"
                    onMouseEnter={Array.isArray(item.children) && item.children.length > 0 ? armSubmenu : undefined}
                  >
                    <Link
                      to={item.href}
                      className={`relative flex items-center justify-center gap-1.5 px-2 py-3 text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                        isActive ? 'text-primary bg-primary/[0.06]' : 'text-muted-foreground hover:text-primary hover:bg-primary/[0.04]'
                      }`}
                    >
                      <Icon size={16} className="shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5" />
                      <span className="truncate">{item.name}</span>
                      <span
                        className={`pointer-events-none absolute left-4 right-4 bottom-1.5 h-[2px] rounded-full bg-primary transition-transform duration-300 ease-out origin-center ${
                          isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                        }`}
                      />
                    </Link>
                    {Array.isArray(item.children) && item.children.length > 0 && (
                      <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-150 absolute left-1/2 -translate-x-1/2 mt-1 min-w-[200px] bg-popover text-popover-foreground border border-border rounded-md shadow-lg p-2 z-50">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={hideOnSubItem}
                            className="block px-3 py-2 rounded hover:bg-primary/5 hover:text-primary text-sm whitespace-nowrap"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Outils fondateur (rangée dédiée) */}
            {((user && user.user_type === 'fondateur') || isFounder) && (
              <div className="flex items-center justify-center gap-2 pt-2">
                {user && user.user_type === 'fondateur' && (
                  <button
                    onClick={async () => {
                      try {
                        setMaintLoading(true);
                        const res = await adminSaveMaintenance({ enabled: !maint, message: !maint ? 'Site en maintenance, merci de revenir bientôt.' : null }, token);
                        setMaint(!!res.enabled);
                      } catch (e) {
                        console.error('Erreur maintenance:', e);
                      } finally {
                        setMaintLoading(false);
                      }
                    }}
                    className={`px-2.5 py-1.5 rounded-md text-sm font-medium border whitespace-nowrap ${maint ? 'bg-destructive text-destructive-foreground border-destructive' : 'border-border hover:bg-primary/5 text-muted-foreground'}`}
                    title="Activer/Désactiver la maintenance"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Shield size={16} />
                      {maintLoading ? '...' : (maint ? 'Maintenance ON' : 'Maintenance OFF')}
                    </span>
                  </button>
                )}
                {isFounder && (
                  <button
                    onClick={() => {
                      try {
                        const current = localStorage.getItem('padaid_preview_mode') === 'true';
                        const next = (!current).toString();
                        localStorage.setItem('padaid_preview_mode', next);
                        if (next === 'true') {
                          localStorage.removeItem('padaid_admin_token');
                          localStorage.removeItem('padaid_user_token');
                        }
                        window.location.href = next === 'true' ? '/auth' : '/home';
                      } catch (e) {
                        console.error('Erreur toggle preview:', e);
                      }
                    }}
                    className="px-2.5 py-1.5 rounded-md text-sm font-medium border border-border hover:bg-primary/5 text-muted-foreground whitespace-nowrap"
                    title="Basculer en mode aperçu externe"
                    data-testid="preview-mode-btn"
                  >
                    {t('common.preview_mode')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-background/95 backdrop-blur border-t border-border">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                const expanded = mobileExpanded === item.name;
                return (
                  <div key={item.name}>
                    <div className="flex items-center">
                      <Link
                        to={item.href}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium transition-colors flex-1 ${
                          isActive ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        <Icon size={20} />
                        <span>{item.name}</span>
                      </Link>
                      {hasChildren && (
                        <button
                          onClick={() => setMobileExpanded(expanded ? null : item.name)}
                          className="p-2 text-muted-foreground hover:text-primary"
                          aria-label={`Ouvrir le sous-menu ${item.name}`}
                          data-testid={`mobile-submenu-toggle-${item.name}`}
                        >
                          <ChevronDown size={18} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>
                    {hasChildren && expanded && (
                      <div className="ml-8 border-l border-border pl-3 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={hideOnSubItem}
                            className="block px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-primary hover:bg-primary/5"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link 
                to="/payment-v2" 
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200 text-sm font-medium mx-3 mt-2 block text-center"
                onClick={() => setIsOpen(false)}
              >
                {t('common.support_padaid')}
              </Link>
              {isLoggedIn ? (
                <button
                  onClick={() => { setIsOpen(false); handleLogout(); }}
                  className="bg-destructive/10 text-destructive px-4 py-2 rounded-lg hover:bg-destructive/20 transition-colors duration-200 text-sm font-medium mx-3 mt-2 w-[calc(100%-1.5rem)] flex items-center justify-center gap-2"
                  data-testid="logout-btn-mobile"
                >
                  <LogOut size={16} /> {t('common.logout')}
                </button>
              ) : (
                <Link 
                  to="/admin/login" 
                  className="bg-secondary text-secondary-foreground px-4 py-2 rounded-lg hover:bg-secondary/90 transition-colors duration-200 text-sm font-medium mx-3 mt-2 block text-center"
                  onClick={() => setIsOpen(false)}
                >
                  {t('common.admin')}
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;